import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Shield, Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

interface AdminMFAVerifyProps {
  onVerified: () => void;
}

export function AdminMFAVerify({ onVerified }: AdminMFAVerifyProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [factorId, setFactorId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getFactorId();
  }, []);

  const getFactorId = async () => {
    try {
      const { data: factors, error } = await supabase.auth.mfa.listFactors();
      
      if (error) throw error;

      const totpFactor = factors?.totp?.find(f => f.status === 'verified');
      if (totpFactor) {
        setFactorId(totpFactor.id);
      }
    } catch (error) {
      console.error("Error getting factor:", error);
    }
  };

  const handleVerify = async () => {
    if (!factorId || verificationCode.length !== 6) {
      setError("Veuillez entrer un code à 6 chiffres");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId,
      });

      if (challengeError) throw challengeError;

      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challengeData.id,
        code: verificationCode,
      });

      if (verifyError) throw verifyError;

      toast.success("Authentification réussie !");
      onVerified();
    } catch (error: any) {
      console.error("Error verifying MFA:", error);
      setError("Code invalide. Veuillez réessayer.");
      setVerificationCode("");
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && verificationCode.length === 6) {
      handleVerify();
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 p-3 bg-primary/10 rounded-full w-fit">
            <Shield className="h-10 w-10 text-primary" />
          </div>
          <CardTitle className="text-xl">Vérification 2FA</CardTitle>
          <CardDescription>
            Entrez le code de votre application d'authentification pour accéder à l'espace admin.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {error && (
            <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3 flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-destructive flex-shrink-0" />
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          <div className="space-y-2">
            <label className="text-sm font-medium">Code d'authentification</label>
            <Input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              placeholder="000000"
              value={verificationCode}
              onChange={(e) => {
                setVerificationCode(e.target.value.replace(/\D/g, ''));
                setError(null);
              }}
              onKeyPress={handleKeyPress}
              className="text-center text-2xl tracking-widest font-mono"
              autoFocus
            />
            <p className="text-xs text-muted-foreground text-center">
              Entrez le code à 6 chiffres de votre application
            </p>
          </div>

          <Button 
            className="w-full" 
            onClick={handleVerify}
            disabled={isLoading || verificationCode.length !== 6}
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Vérification...
              </>
            ) : (
              "Vérifier"
            )}
          </Button>

          <p className="text-xs text-center text-muted-foreground">
            Vous n'avez pas accès à votre application ?{" "}
            <a href="/contact" className="text-primary hover:underline">
              Contactez le support
            </a>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
